import {ReactNode, useEffect, useState} from "react";
import classNames from "classnames";
import {useModifiedTranslation} from "hooks/useModifiedTranslation";
import {createPortal} from "react-dom";
import {BiChevronLeftCircle} from "react-icons/bi";
import {Card} from "./Card";
import {CloseButton} from "./CloseButton";

interface ModalProps {
  close: () => void;
  heading?: ReactNode;
  children?: ReactNode;
  fullScreen?: boolean;
}
export const Modal = ({ close, children, heading, fullScreen = false }: ModalProps) => {
  const { t } = useModifiedTranslation();
  const [visible, setVisible] = useState(false);

  const closeModal = () => {
    setVisible(false);
    setTimeout(() => {
      close();
    }, 200);
  };

  useEffect(() => {
    setVisible(true);
    document.body.style.overflowY = "hidden";
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close(); // This will now call the `closeModal` function from `BuyModal`
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflowY = "";
    };
  }, [close]);

  if (fullScreen) {
    return createPortal(
      <div
        className={classNames(
          "w-full h-full fixed opacity-0 top-0 left-0 z-[500] bg-white transition-opacity overflow-y-auto",
          { "opacity-100": visible }
        )}>
        <div className="sticky top-0 left-0 w-full bg-white z-[501]">
          <div className="container flex justify-between px-2 pt-4 pb-2 m-auto mb-2 w-5/6 h-full border-b">
            <div
              data-dismiss="modal"
              aria-label="Close"
              onClick={() => closeModal()}
              className="flex gap-2 items-center p-1 text-xl font-bold hover:bg-gray-100 hover:rounded-lg cursor-pointer">
              <BiChevronLeftCircle tabIndex={1} name="test" /> {t("modal.back")}
            </div>
            <CloseButton onClick={() => closeModal()} />
          </div>
        </div>
        <div className="container m-auto w-5/6 h-full">{children}</div>
      </div>,
      document.getElementById("root") ?? document.body
    );
  }

  return createPortal(
    <div
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      onClick={(e) => {
        e.preventDefault();
        closeModal();
      }}
      className={classNames(
        "z-[400] bg-gray-300/50 opacity-0 fixed top-0 left-0 w-full h-full flex items-center justify-center transition-opacity",
        { "opacity-100": visible }
      )}>
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        className="fixed top-1/2 left-1/2 w-4/5 md:w-3/5 lg:w-3/5 xl:w-2/5 -translate-x-1/2 -translate-y-1/2 z-[500] max-w-[800px]">
        <Card options={{ heading: { size: "sm" } }}>
          <div className="flex justify-end px-2 pt-4 pb-2 mb-2 w-full border-b">
            <div className="z-[501]">
              <CloseButton onClick={() => closeModal()} />
            </div>
            {heading && (
              <div className="flex absolute top-0 items-center px-2 pt-4 pb-2 mb-2 w-full">
                <div className="flex justify-start items-center pl-6 w-full h-full text-xl font-bold">
                  {heading}
                </div>
              </div>
            )}
          </div>

          {children}
        </Card>
      </div>
    </div>,
    document.getElementById("root") ?? document.body
  );
};
